import type { SetupSection } from './fieldTypes';

const WHOLE = { regex: /^\d+$/, message: 'Whole number' };
const RELEASE = [
  { value: 'AUTO', label: 'Send automatically' },
  { value: 'APPROVAL', label: 'Hold for approval before release' },
];

export const ACTIVATE_SECTION: SetupSection = {
  id: 'activate',
  group: 'ACTIVATE',
  title: 'Activate register',
  summary: 'Create the trust and its class on the register, ready to take applications.',
  blocks: [],
};

export const OFFER_SECTIONS: SetupSection[] = [
  {
    id: 'offer',
    group: 'OFFER',
    title: 'Offer overview',
    summary: 'What investors see: the offer page, documents and closing date.',
    blocks: [
      {
        title: 'Offer',
        fields: [
          { key: 'offer.title', label: 'Offer title', kind: 'text', required: true, wide: true },
          { key: 'offer.url', label: 'Offer link', kind: 'text', required: true, wide: true },
          { key: 'offer.summary', label: 'Summary for investors', kind: 'textarea', wide: true },
        ],
      },
      {
        title: 'Closing date',
        fields: [
          { key: 'offer.closeDate', label: 'Applications close', kind: 'date', required: true },
          { key: 'offer.closeTime', label: 'Time', kind: 'text', required: true },
          {
            key: 'offer.timezone',
            label: 'Time zone',
            kind: 'select',
            options: [
              { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
              { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
              { value: 'Australia/Perth', label: 'Perth (AWST)' },
            ],
          },
          { key: 'offer.countdown', label: 'Show a countdown on the offer page', kind: 'switch' },
          {
            key: 'offer.issueAtClose',
            label: 'Issue all units together at financial close',
            kind: 'switch',
            wide: true,
            help: 'Money is collected first; units are issued once the fund holds the asset.',
          },
        ],
      },
      {
        title: 'Investor centre',
        fields: [
          {
            key: 'offer.useExisting',
            label: 'Show the offer to existing investors, who apply with the details on file',
            kind: 'switch',
            wide: true,
          },
          {
            key: 'offer.publicApply',
            label: 'Let new investors apply straight from the public offer link',
            kind: 'switch',
            wide: true,
            help: 'Usually off: Fides brings new investors on board itself.',
          },
          {
            key: 'offer.notesField',
            label: 'Ask applicants for notes',
            kind: 'switch',
            wide: true,
          },
        ],
      },
    ],
  },
  {
    id: 'terms',
    group: 'OFFER',
    title: 'Terms & due diligence',
    summary: 'What applicants agree to and confirm before they can submit.',
    blocks: [
      {
        title: 'Terms and conditions',
        fields: [
          {
            key: 'offer.terms',
            label: 'Terms applicants accept',
            kind: 'textarea',
            required: true,
            wide: true,
            toConfirm: true,
          },
        ],
      },
      {
        title: 'Due diligence confirmations',
        fields: [
          {
            key: 'offer.ddReadIm',
            label: 'Has read the Information Memorandum',
            kind: 'switch',
            wide: true,
          },
          {
            key: 'offer.ddWholesale',
            label: 'Confirms wholesale client status',
            kind: 'switch',
            wide: true,
          },
          { key: 'offer.ddRisks', label: 'Understands the key risks', kind: 'switch', wide: true },
        ],
      },
    ],
  },
  {
    id: 'amount',
    group: 'OFFER',
    title: 'Investment & payment',
    summary: 'Amounts, priority access, verification, bank details and how investors pay.',
    blocks: [
      {
        title: 'Investment amount',
        fields: [
          {
            key: 'offer.min',
            label: 'Minimum ($)',
            kind: 'number',
            required: true,
            pattern: WHOLE,
          },
          { key: 'offer.max', label: 'Maximum ($, optional)', kind: 'number', pattern: WHOLE },
          {
            key: 'offer.increment',
            label: 'Increment ($)',
            kind: 'number',
            required: true,
            pattern: WHOLE,
          },
        ],
      },
      {
        title: 'Priority access',
        fields: [
          {
            key: 'offer.priorityCodes',
            label: 'Use priority codes or lists',
            kind: 'switch',
            wide: true,
            help: 'For distribution networks: tracks the referral fees and rebates owed by code.',
          },
          {
            key: 'offer.priorityList',
            label: 'Priority codes (one per line)',
            kind: 'textarea',
            wide: true,
            required: true,
            showIf: (values) => values['offer.priorityCodes'] === true,
          },
        ],
      },
      {
        title: 'Verification and bank details',
        description: 'Verification follows the fund’s investor rules.',
        fields: [
          {
            key: 'offer.collectBank',
            label: 'Collect bank details for distributions',
            kind: 'switch',
            wide: true,
          },
        ],
      },
      {
        title: 'Payable on application',
        description:
          'What investors pay now for each unit. Less than the issue price makes the units partly paid; the balance is called later.',
        fields: [
          {
            key: 'offer.payableNow',
            label: 'Payable now ($ per unit)',
            kind: 'text',
            required: true,
            pattern: { regex: /^\d+(\.\d{1,4})?$/, message: 'Enter an amount, e.g. 0.30' },
          },
        ],
      },
      {
        title: 'Payment method',
        fields: [
          { key: 'offer.payEft', label: 'EFT to the trust account', kind: 'switch' },
          { key: 'offer.payBpay', label: 'BPAY', kind: 'switch' },
          {
            key: 'offer.payDueMode',
            label: 'Payment due',
            kind: 'select',
            options: [
              { value: 'DAYS', label: 'Within a number of days of applying' },
              { value: 'DATE', label: 'By a fixed date' },
            ],
          },
          {
            key: 'offer.payDays',
            label: 'Days after applying',
            kind: 'number',
            required: true,
            pattern: WHOLE,
            showIf: (values) => values['offer.payDueMode'] !== 'DATE',
          },
          {
            key: 'offer.payDate',
            label: 'Pay by',
            kind: 'date',
            required: true,
            showIf: (values) => values['offer.payDueMode'] === 'DATE',
          },
        ],
      },
    ],
  },
  {
    id: 'comms',
    group: 'OFFER',
    title: 'Communications',
    summary: 'Emails to applicants, alerts to the team and offer branding.',
    blocks: [
      {
        title: 'Investor communications',
        fields: [
          {
            key: 'comms.confirmEmail',
            label: 'Send a confirmation email on submission',
            kind: 'switch',
            wide: true,
          },
          {
            key: 'comms.confirmRelease',
            label: 'Confirmations and payment instructions',
            kind: 'select',
            options: RELEASE,
          },
          {
            key: 'comms.reminderRelease',
            label: 'Payment reminders',
            kind: 'select',
            options: RELEASE,
          },
          {
            key: 'comms.scaleBackRelease',
            label: 'Scale-back notices',
            kind: 'select',
            options: RELEASE,
          },
          {
            key: 'comms.rejectedRelease',
            label: 'Application rejected',
            kind: 'select',
            options: RELEASE,
          },
          {
            key: 'comms.unitsIssuedRelease',
            label: 'Units issued',
            kind: 'select',
            options: RELEASE,
          },
          {
            key: 'comms.certificateRelease',
            label: 'Certificate released',
            kind: 'select',
            options: RELEASE,
          },
          {
            key: 'comms.reminderDays',
            label: 'Remind unpaid applicants after (days)',
            kind: 'number',
            pattern: WHOLE,
          },
          { key: 'comms.fromName', label: 'From name', kind: 'text' },
          { key: 'comms.replyTo', label: 'Reply-to email', kind: 'text' },
        ],
      },
      {
        title: 'Notifications to the team',
        fields: [
          {
            key: 'notify.newApplication',
            label: 'New application alerts to',
            kind: 'text',
            required: true,
          },
          { key: 'notify.paymentReceived', label: 'Alert when money is received', kind: 'switch' },
          { key: 'notify.dailySummary', label: 'Daily summary email', kind: 'switch' },
        ],
      },
      {
        title: 'Branding',
        fields: [
          {
            key: 'offer.branding',
            label: 'Offer branding',
            kind: 'select',
            options: [
              { value: 'INHERIT', label: 'Use the fund’s branding' },
              { value: 'FIDES', label: 'Default branding' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'review',
    group: 'OFFER',
    title: 'Review & publish',
    summary: 'Check everything, then open the offer to investors.',
    blocks: [],
  },
];
